// ─── CONSTANTES ───────────────────────────────────────────────────────────────
const BASE_URL          = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
const KEY_ACCESS_TOKEN  = 'lavex_access_token';
const KEY_REFRESH_TOKEN = 'lavex_refresh_token';
const KEY_USER          = 'lavex_user';

// Événement émis quand la session expire — AuthContext l'écoute et redirige vers /login
export const AUTH_EXPIRED_EVENT = 'lavex:auth_expired';

// ─── TOKEN MANAGEMENT ─────────────────────────────────────────────────────────
export const getToken        = () => localStorage.getItem(KEY_ACCESS_TOKEN);
export const getRefreshToken = () => localStorage.getItem(KEY_REFRESH_TOKEN);

export const setTokens = (access, refresh) => {
  if (access)  localStorage.setItem(KEY_ACCESS_TOKEN,  access);
  if (refresh) localStorage.setItem(KEY_REFRESH_TOKEN, refresh);
};

export const clearTokens = () => {
  localStorage.removeItem(KEY_ACCESS_TOKEN);
  localStorage.removeItem(KEY_REFRESH_TOKEN);
  localStorage.removeItem(KEY_USER);
};

const handleAuthExpired = () => {
  clearTokens();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

// ─── VERROU ANTI-REFRESH CONCURRENT ──────────────────────────────────────────
let refreshPromise = null;

const refreshAccessToken = async () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const rt = getRefreshToken();
    if (!rt) throw new Error('No refresh token');

    const res = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ refreshToken: rt }),
    });

    let data;
    try   { data = await res.json(); }
    catch { throw new Error('Refresh: réponse non-JSON'); }

    if (!res.ok || !data?.data?.accessToken) throw new Error('Refresh failed');

    setTokens(data.data.accessToken, data.data.refreshToken ?? null);
    return data.data.accessToken;
  })().finally(() => { refreshPromise = null; });

  return refreshPromise;
};

// ─── BASE REQUEST ─────────────────────────────────────────────────────────────
const request = async (endpoint, options = {}) => {
  const makeHeaders = (token) => ({
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  });

  let res = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: makeHeaders(getToken()),
  });

  if (res.status === 401) {
    try {
      const newToken = await refreshAccessToken();
      res = await fetch(`${BASE_URL}${endpoint}`, {
        ...options,
        headers: makeHeaders(newToken),
      });
    } catch {
      handleAuthExpired();
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
  }

  let data;
  let rawText;
  try {
    rawText = await res.text();
    data    = JSON.parse(rawText);
  } catch {
    const preview = rawText
      ? rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 120)
      : `HTTP ${res.status}`;
    throw new Error(`Réponse invalide du serveur (${res.status}) : ${preview}`);
  }

  if (!res.ok) throw new Error(data?.message || `Erreur serveur ${res.status}`);
  return data;
};

// ─── HELPERS HTTP ─────────────────────────────────────────────────────────────
const buildQS = (params) =>
  params ? '?' + new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null))
  ).toString() : '';

const get  = (url, params) => request(`${url}${buildQS(params)}`);
const post = (url, body)   => request(url, { method: 'POST',   body: JSON.stringify(body) });
const put  = (url, body)   => request(url, { method: 'PUT',    body: JSON.stringify(body) });
const del  = (url, body)   => request(url, { method: 'DELETE', ...(body ? { body: JSON.stringify(body) } : {}) });

// Upload multipart/form-data (sans Content-Type forcé — browser le définit)
const upload = (url, formData) => {
  const token = localStorage.getItem(KEY_ACCESS_TOKEN);
  return fetch(`${BASE_URL}${url}`, {
    method:  'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body:    formData,
  }).then(async r => {
    let data;
    try   { data = await r.json(); }
    catch { throw new Error(`Upload échoué (${r.status})`); }
    if (!r.ok) throw new Error(data?.message || `Erreur ${r.status}`);
    return data;
  });
};

// ─── AUTH ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login:          (body) => post('/auth/login', body),
  logout:         (body) => post('/auth/logout', body),
  refreshToken:   (body) => post('/auth/refresh-token', body),
  updatePassword: (body) => put('/auth/update-password', body),
};

// ─── ADMIN ────────────────────────────────────────────────────────────────────
export const adminApi = {

  // ── DASHBOARD ────────────────────────────────────────────────────────────────
  getDashboard: () => get('/admin/dashboard'),

  // ── VALIDATIONS COMPTES (app mobile — catégorie A) ────────────────────────────
  getPendingValidations: ()          => get('/admin/pending-validations'),
  validateUser:          (id, body)  => put(`/admin/users/${id}/validate`, body),

  // ── UTILISATEURS ──────────────────────────────────────────────────────────────
  updateUser:         (id, body) => put(`/admin/users/${id}`, body),
  changeUserCategory: (id, body) => put(`/admin/users/${id}/category`, body),
  deleteUser:         (id, permanent = false) => del(`/admin/users/${id}`, { permanent }),

  // ── LISTES ADMIN ──────────────────────────────────────────────────────────────
  getLaveriesList: (params) => get('/admin/laveries', params),
  getLivreursList: (params) => get('/admin/livreurs', params),
  getClientsList:  (params) => get('/admin/clients',  params),

  // ── LAVERIES ──────────────────────────────────────────────────────────────────
  createLaverie:       (body)     => post('/admin/laveries', body),
  updateLaverie:       (id, body) => put(`/admin/laveries/${id}`, body),
  updateLaverieQuotas: (id, body) => put(`/admin/laveries/${id}/quotas`, body),
  deleteLaverie:       (id, permanent = false) => del(`/admin/laveries/${id}`, { permanent }),

  // Commandes en attente de réception à la laverie (v2.4)
  getLaveriePendingReception: (id) => get(`/admin/laveries/${id}/pending-reception`),

  // ── SERVICES LAVERIES ─────────────────────────────────────────────────────────
  addService:    (lavId, body)      => post(`/admin/laveries/${lavId}/services`, body),
  updateService: (lavId, sid, body) => put(`/admin/laveries/${lavId}/services/${sid}`, body),
  deleteService: (lavId, sid)       => del(`/admin/laveries/${lavId}/services/${sid}`),

  // Demandes de services (laveries → admin) (v2.2)
  getServiceRequests:    (params)   => get('/admin/services/requests', params),
  approveServiceRequest: (id, body) => post(`/admin/services/requests/${id}/approve`, body),
  rejectServiceRequest:  (id, body) => post(`/admin/services/requests/${id}/reject`, body),

  // ── CATÉGORIES DE SERVICES (v3.0) ─────────────────────────────────────────────
  getServiceCategories:  ()         => get('/admin/service-categories'),
  createServiceCategory: (body)     => post('/admin/service-categories', body),
  updateServiceCategory: (id, body) => put(`/admin/service-categories/${id}`, body),
  deleteServiceCategory: (id)       => del(`/admin/service-categories/${id}`),

  // ── PRICING ───────────────────────────────────────────────────────────────────
  getPricing:    ()       => get('/admin/pricing'),
  setPricing:    (body)   => put('/admin/pricing', body),

  // Config globale pricing pièce/kg (v2.4)
  getPricingConfig: ()       => get('/admin/pricing/config'),
  setPricingConfig: (body)   => put('/admin/pricing/config', body),
  getPricingStats:  (params) => get('/admin/pricing/stats', params),

  // Prix par service (v2.4)
  getServicesPricing:       (params)     => get('/admin/services/pricing', params),
  updateServicePricing:     (id, body)   => put(`/admin/services/${id}/pricing`, body),
  bulkUpdateServicePricing: (body)       => put('/admin/services/pricing/bulk', body),
  getServicePricingHistory: (id, params) => get(`/admin/services/${id}/pricing/history`, params),

  // ── LIVREURS ──────────────────────────────────────────────────────────────────
  createLivreur:             (body)     => post('/admin/livreurs', body),
  updateLivreur:             (id, body) => put(`/admin/livreurs/${id}`, body),
  updateLivreurQuotas:       (id, body) => put(`/admin/livreurs/${id}/quotas`, body),
  updateLivreurAvailability: (id, body) => put(`/admin/livreurs/${id}/availability`, body),
  deleteLivreur:             (id, permanent = false) => del(`/admin/livreurs/${id}`, { permanent }),

  // ── STATS ─────────────────────────────────────────────────────────────────────
  getLaverieStats: (id) => get(`/admin/stats/laverie/${id}`),
  getLivreurStats: (id) => get(`/admin/stats/livreur/${id}`),
  getClientStats:  (id) => get(`/admin/stats/client/${id}`),

  // ── COMMANDES ─────────────────────────────────────────────────────────────────
  getOrderPrintData: (orderId) => get(`/admin/laveries/orders/${orderId}/print`),

  // ── CODES PROMO / FIDÉLITÉ ────────────────────────────────────────────────────
  createDiscount:         (body)   => post('/admin/discounts', body),
  createLoyaltyDiscounts: (body)   => post('/admin/discounts/loyal-customers', body),
  getDiscountsList:       (params) => get('/admin/discounts', params),

  // ── SUPPORT (v2.2) ────────────────────────────────────────────────────────────
  getSupportTickets:  (params)   => get('/admin/support/tickets', params),
  getSupportStats:    ()         => get('/admin/support/stats'),
  updateTicket:       (id, body) => put(`/admin/support/tickets/${id}`, body),
  getTicketMessages:  (id)       => get(`/support/tickets/${id}`),
  sendTicketMessage:  (id, body) => post(`/support/tickets/${id}/messages`, body),

  // ── DEMANDES ENTREPRISE v2.2 (formulaire public, ancien workflow) ─────────────
  getEnterpriseRequests:    (params)   => get('/admin/enterprise/requests', params),
  approveEnterpriseRequest: (id, body) => post(`/admin/enterprise/requests/${id}/approve`, body),
  rejectEnterpriseRequest:  (id, body) => post(`/admin/enterprise/requests/${id}/reject`, body),

  // ── PIPELINE ENTREPRISE v3.2 (nouveau workflow 8 étapes) ──────────────────────
  getEnterprisePipeline:     (params) => get('/admin/enterprise/requests/pipeline', params),
  getEnterpriseWorkflow:     (id)     => get(`/admin/enterprise/requests/${id}/workflow-status`),
  // Étape 4 : l'admin confirme avoir appelé le contact
  phoneConfirmEnterprise:    (id, body = {}) => put(`/admin/enterprise/requests/${id}/phone-confirm`, body),
  // Étape 5 : RDV physique — meeting_date (ISO) + agent_id (number, optionnel)
  scheduleMeetingEnterprise: (id, body) => put(`/admin/enterprise/requests/${id}/schedule-meeting`, body),
  // Étape 6 : légalisation — legalization_date (ISO) + notary_name (string, optionnel)
  legalizeEnterprise:        (id, body = {}) => put(`/admin/enterprise/requests/${id}/legalize`, body),
  // Étape 7 : approbation finale — admin_notes (string, optionnel)
  approveFinalEnterprise:    (id, body = {}) => put(`/admin/enterprise/requests/${id}/approve-final`, body),
  // Étape 8 : création du compte + envoi email mot de passe temporaire
  openAccountEnterprise:     (id)     => post(`/admin/enterprise/requests/${id}/open-account`),
  // Rejet à n'importe quelle étape — utilise rejection_reason (nom unifié backend)
  rejectEnterpriseWorkflow:  (id, body) => put(`/admin/enterprise/requests/${id}/reject`, {
    rejection_reason: body?.reason || body?.rejection_reason || '',
  }),

  // ── CONTRAT PDF (v3.2) ────────────────────────────────────────────────────────
  // GET /admin/enterprise/contract → { available, version, uploaded_at, original_name }
  getContractMeta:  ()         => get('/admin/enterprise/contract'),
  uploadContract:   (formData) => upload('/admin/enterprise/contract/upload', formData),

  // ── RÈGLEMENTS (v3.0) ─────────────────────────────────────────────────────────
  getLivreurSettlements:       (params) => get('/admin/settlements/livreurs', params),
  confirmLivreurSettlement:    (id)     => put(`/admin/settlements/livreurs/${id}/confirm`),
  settleNegativeLivreur:       (id)     => put(`/admin/settlements/livreurs/${id}/settle-negative`),
  getLaverieSettlements:       (params) => get('/admin/settlements/laveries', params),
  settleLaverie:               (id)     => put(`/admin/settlements/laveries/${id}/settle`),
  getEnterpriseSettlements:    (params) => get('/admin/settlements/enterprises', params),
  confirmEnterpriseSettlement: (id)     => put(`/admin/settlements/enterprises/${id}/confirm`),
  // Facture : répond { data: { invoice_html } } ou { data: { pdf_url } }
  getSettlementInvoice:        (type, id) => get(`/admin/settlements/${type}/${id}/invoice`),

  // ── SUPERVISION FINANCIÈRE (v3.0) ─────────────────────────────────────────────
  // Flux temps réel : espèces livreurs, profit net laveries, créances entreprises
  getWalletOverview: () => get('/admin/wallet/overview'),
  // Historique mouvements d'un utilisateur (cartable)
  getUserLedger:     (userId, params) => get(`/admin/wallet/ledger/${userId}`, params),

  // ── RAPPORTS QR (v2.4) ────────────────────────────────────────────────────────
  getQRPerformanceReport: (params) => get('/admin/reports/qr-performance', params),
};

// ─── ORDERS ───────────────────────────────────────────────────────────────────
export const ordersApi = {
  list:   (params)   => get('/orders', params),
  get:    (id)       => get(`/orders/${id}`),
  cancel: (id, body) => put(`/orders/${id}/cancel`, body),
};

// ─── LAVERIES (public) ────────────────────────────────────────────────────────
export const laveriesApi = {
  list: (params) => get('/laveries', params),
  get:  (id)     => get(`/laveries/${id}`),
};

// ─── USERS ────────────────────────────────────────────────────────────────────
export const usersApi = {
  getProfile:           ()       => get('/users/profile'),
  updateProfile:        (body)   => put('/users/profile', body),
  getNotifications:     (params) => get('/users/notifications', params),
  markNotificationRead: (id)     => put(`/users/notifications/${id}/read`),
  markAllRead:          ()       => put('/users/notifications/read-all'),
  getPointsHistory:     (params) => get('/users/points-history', params),
};